import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LANGUAGE_LABELS, type Language } from "@/lib/i18n";
import { getLanguageMeta } from "@/lib/languageMeta";

export interface AvailableLanguage {
  code: string;
  label: string;
  nativeName: string;
  flag: string;
  completion: number;
  isComplete: boolean;
}

/**
 * Returns the list of languages that should be shown in the language switcher.
 * Source priority:
 *  1) `supported_languages` rows where is_active = true (DB-driven, dynamic)
 *  2) Average translation completion across namespaces from `ui_translations`
 *  3) Fallback: hardcoded LANGUAGE_LABELS so the switcher never goes empty.
 */
export function useAvailableLanguages() {
  return useQuery({
    queryKey: ["available-languages"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<AvailableLanguage[]> => {
      // Try DB first
      const [{ data: langs }, { data: trans }] = await Promise.all([
        supabase
          .from("supported_languages" as any)
          .select("code, english_name, native_name, flag_emoji, is_active")
          .eq("is_active", true),
        supabase
          .from("ui_translations" as any)
          .select("language_code, completion_percent, status"),
      ]);

      // Fallback to inline LANGUAGE_LABELS when table not yet populated
      if (!langs || langs.length === 0) {
        return (Object.keys(LANGUAGE_LABELS) as Language[]).map((code) => {
          const meta = getLanguageMeta(code);
          return {
            code,
            label: LANGUAGE_LABELS[code],
            nativeName: meta?.nativeName ?? LANGUAGE_LABELS[code],
            flag: meta?.flag ?? "🌐",
            completion: 100,
            isComplete: true,
          };
        });
      }

      // Build completion map per language
      const completionByLang = new Map<string, number[]>();
      (trans ?? []).forEach((row: any) => {
        const arr = completionByLang.get(row.language_code) ?? [];
        arr.push(Number(row.completion_percent ?? 0));
        completionByLang.set(row.language_code, arr);
      });

      return langs.map((l: any) => {
        const arr = completionByLang.get(l.code) ?? [];
        const completion = arr.length
          ? Math.round(arr.reduce((s, v) => s + v, 0) / arr.length)
          : (l.code === "en" ? 100 : 0);
        const meta = getLanguageMeta(l.code);
        return {
          code: l.code,
          label: l.english_name ?? meta?.englishName ?? l.code,
          nativeName: l.native_name ?? meta?.nativeName ?? l.code,
          flag: l.flag_emoji ?? meta?.flag ?? "🌐",
          completion,
          // English source is always considered complete
          isComplete: l.code === "en" || completion >= 80,
        };
      });
    },
  });
}
